import React from 'react';
import { Text, Container, Sticky, Link, Message, useBreakpoint } from 'basis';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { Header } from 'components/header/Header';
import { Footer } from 'components/footer/Footer';
import { YellowMessage } from 'components/message/YellowMessage';
import { config, PRODUCT_CODE_DAVID_JONES_PREMIERE, PRODUCT_CODE_DAVID_JONES_PRESTIGE } from '_config';
import { getFeatureValue } from 'featureToggles';
import { isDJ } from 'utils/byConfig';
import DJBanner from 'images/banner/dj-background.webp';
import PremiereCardImg from 'images/premiere-card.png';
import PrestigeCardImg from 'images/prestige-card.png';
import PremiereCardMobile from 'images/banner/dj-premiere-mobile.webp';
import PrestigeCardMobile from 'images/banner/dj-prestige-mobile.webp';
import {
  Banner,
  BannerLeft,
  BannerRight,
  BannerImage,
  HeaderBannerTextStyle,
  ProductNameWrapper,
} from 'views/landing-page-dj/LandingPageDJ.styles';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  width: 100%;
`;

const ContentWrapper = styled.div`
  max-width: 600px;
  min-height: 500px;
  margin: ${props => (props.margin ? props.margin : '16px')};
`;

const BannerLink = styled.a`
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

// Banner sits under the header; min-height keeps the footer lower, like the original maintenance page.
// position/z-index give it its own stacking context so the z-index -1 girl image isn't hidden behind the white background.
const BannerPage = styled.div`
  min-height: 600px;
  background: white;
  position: relative;
  z-index: 0;
`;

// Only allow https links from the flag
const isHttpsLink = link => /^https:\/\//i.test(link || '');

function DJMaintenanceBanner({ notice, productCode }) {
  const theme = useTheme();
  const breakpoint = useBreakpoint();
  const isDesktop = ['md', 'lg', 'xl'].includes(breakpoint);
  const isPrestige = productCode === PRODUCT_CODE_DAVID_JONES_PRESTIGE;
  const desktopCard = isPrestige ? PrestigeCardImg : PremiereCardImg;
  const mobileCard = isPrestige ? PrestigeCardMobile : PremiereCardMobile;

  return (
    <Banner theme={theme}>
      <Container width="100%" padding="0" padding-lg="0">
        <BannerLeft theme={theme}>
          <img src={DJBanner} alt="" />
        </BannerLeft>
        <BannerRight theme={theme}>
          <HeaderBannerTextStyle theme={theme}>
            <Container
              padding="0 4"
              padding-xs="0 6 6 5"
              padding-md="0 0 6 0"
              margin="0"
              margin-md="0 0 0 5"
              margin-sm="0 0 10 5"
              width="100%"
              width-sm="auto"
            >
              <Container margin="3 0 4 0">
                <ProductNameWrapper theme={theme}>{notice.title}</ProductNameWrapper>
              </Container>
              {isHttpsLink(notice.link) && (
                <BannerLink href={notice.link} target="_blank" rel="noopener noreferrer">
                  {notice.linkText || 'Find out more.'}
                </BannerLink>
              )}
            </Container>
          </HeaderBannerTextStyle>
          <BannerImage>
            <img src={isDesktop ? desktopCard : mobileCard} alt="David Jones credit card" />
          </BannerImage>
        </BannerRight>
      </Container>
    </Banner>
  );
}

function MaintenanceNotice({ notice }) {
  const textColor = isDJ() ? 'indigo' : undefined;

  return (
    <YellowMessage>
      <Message testId="yellow-message" severity="warning-or-significant">
        <Text color={textColor}>
          <b>{notice.title}</b>
        </Text>
        {isHttpsLink(notice.link) && (
          <Text margin="2 0 0 0">
            <Link href={notice.link} newTab>
              {notice.linkText || 'Find out more.'}
            </Link>
          </Text>
        )}
      </Message>
    </YellowMessage>
  );
}

export function Maintenance() {
  const theme = useTheme();
  // DJ pages without a product code (comparison page, QR links) use the Premiere message
  const productCode = config.productCode || (isDJ() ? PRODUCT_CODE_DAVID_JONES_PREMIERE : undefined);
  const message = getFeatureValue('maintenanceMessage')?.[productCode];
  const notice = message?.title ? message : null;

  return (
    <>
      <Sticky>
        <Sticky.Item>
          <Header />
        </Sticky.Item>
        {notice && isDJ() ? (
          <BannerPage>
            <DJMaintenanceBanner notice={notice} productCode={productCode} />
          </BannerPage>
        ) : (
          <Container padding="12 0" testId="error" hasBreakpointWidth>
            <Wrapper>
              <ContentWrapper>
                {notice ? (
                  <MaintenanceNotice notice={notice} />
                ) : (
                  <>
                    <Container padding="8 0 0 0">
                      <Text as="h5" textStyle="heading5" color={theme.headingColor} align="left">
                        Our application is currently undergoing maintenance
                      </Text>
                      <p hidden>CF flush from inactive is working</p>
                    </Container>

                    <Container padding="8 0">
                      <Text as="p">{typeof message === 'string' ? message : null}</Text>
                    </Container>
                  </>
                )}
              </ContentWrapper>
            </Wrapper>
          </Container>
        )}
      </Sticky>
      <Footer footer="OVERVIEW" />
    </>
  );
}
